import * as React from 'react';
import Media from 'react-media';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Loader from '../_common/loader';
import Modal from '../_common/modal';
import Popup from '../_common/popup';
import SnippetProvider from '../_providers/snippet-provider/snippet-provider';
import { getText, getConfig, htmlToJson } from '../../helpers';
import keyPages from '../../utilities/key-pages';
import DownloadFile from '../_common/download-file';
import SectionTitle from '../_common/section-title';
import { ReactComponent as TableIcon } from '../../assets/svg/icons/table.svg';
import { ReactComponent as BookIcon } from '../../assets/svg/icons/book.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/icons/info.svg';
import SPGVariables from '../../utilities/spg-variables';

const Carousel = React.lazy(() => import('../_common/carousel'));
const spgVariables = SPGVariables();
const DatasetTableGrid = React.lazy(() => import('../dataset-section-components/dataset-table-grid' /* webpackChunkName: "dataset-table-grid" */));
const cmsApiDocumentsUrl = `${spgVariables.CloudFrontURL}/${getConfig('urls.api.cms.documentPath')}`;


const bundleOptions = getConfig('routes.dataset.carouselOptions');
const initialCarouselState = getConfig('routes.dataset.initialCarouselState');
const maxColumnCountAutoWidth = getConfig('routes.dataset.maxColumnCountAutoWidth');
const filetypeName = getText('generic.download');

const TableIconInstance = <TableIcon />;
const BookIconInstance = <BookIcon />;

const dataGridOptions: DataGridOptions = {
    'sample-data': {
        gridKey: 'sample-data',
        title: getText('pages.dataset.sampleDataTitle'),
        icon: TableIconInstance,
        keyPages: keyPages.datasetSampleDataDownload,
        autoWidth: false,
        sampleDataDescription: getText('pages.dataset.sampleDataDescription')
    },
    'data-dictionary': {
        gridKey: 'data-dictionary',
        title: getText('pages.dataset.dataDictionaryTitle'),
        icon: BookIconInstance,
        keyPages: keyPages.datasetDataDictionaryDownload,
        autoWidth: true
    },
    default: {
        gridKey: '',
        title: '',
        icon: null,
        autoWidth: false
    }
};

const datasetDataSection = function DatasetDataSection({
    dataFilePath,
    datasetBundle,
    dataId,
    isBundle,
    isLoggedIn,
    scrollTo,
    dataGridType
}: DatasetDataSectionProps) {
    const downloadFiletype = isBundle ? 'ZIP' : 'CSV';
    const [carouselState, updateCarouselState] = React.useState<{ activeSlide: number; totalSlides: number }>(initialCarouselState);
    const [showPopupSample, setPopupSample] = React.useState<boolean>(false);

    const getTotalCount = () => {
        if (dataGridType === 'sample-data') {
            return datasetBundle.filter(dataset => dataset.sampleDataLink).length;
        }
        if (dataGridType === 'data-dictionary') {
            return datasetBundle.filter(dataset => dataset.dataDictionaryLink).length;
        }
        return 0;
    };

    React.useEffect(() => {
        updateCarouselState({ activeSlide: 1, totalSlides: getTotalCount() });
    }, []);

    const carouselOptions = {
        ...bundleOptions,
        lazyLoad: false,
        beforeChange: (active: number, next: number) => {
            updateCarouselState(state => {
                return { ...state, activeSlide: next + 1 };
            });
        }
    };

    const getDataLinkId = (linkedDataset: DataProfile) => {
        let linkId;
        if (dataGridType === 'sample-data') {
            linkId = linkedDataset.sampleDataLink;
        }
        if (dataGridType === 'data-dictionary') {
            linkId = linkedDataset.dataDictionaryLink;
        }
        return linkId;
    };

    const bundleCarousel = (
        <div className="page-section__inner--carousel">
            <Carousel carouselSettings={carouselOptions}>
                {datasetBundle.map((linkedDataset: DataProfile, index) => {
                    const linkId = getDataLinkId(linkedDataset) || '';
                    return (
                        linkId && (
                            <div key={linkedDataset.name} data-testid={`bundle-carousel-tablegrid-${index}`}>
                                <SnippetProvider<HTMLToJsonArray>
                                    initialData={{}}
                                    isSecured={isLoggedIn}
                                    parser={htmlToJson}
                                    docKey={linkId}
                                    loaderMinHeight="588px"
                                    render={snippet => (
                                        <div className="page-section__inner">
                                            <DatasetTableGrid
                                                gridKey={`${dataGridOptions[dataGridType].gridKey}-${index + 1}`}
                                                title={linkedDataset.name}
                                                data={snippet}
                                                isLoggedInOverlay={!isLoggedIn}
                                                setAutoColumnWidth={
                                                    snippet && snippet.headerRows && snippet.headerRows.length > 0 && snippet.headerRows[0].length > maxColumnCountAutoWidth
                                                        ? dataGridOptions[dataGridType].autoWidth
                                                        : true
                                                }
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        )
                    );
                })}
            </Carousel>
        </div>
    );
    const noBundle = dataId && (
        <SnippetProvider<HTMLToJsonArray>
            initialData={{}}
            isSecured={isLoggedIn}
            parser={htmlToJson}
            docKey={dataId}
            loaderMinHeight="588px"
            render={snippet => (
                <div className="page-section__inner" data-testid={`dataset-table-grid-${dataGridOptions[dataGridType].gridKey}`}>
                    <DatasetTableGrid
                        gridKey={dataGridOptions[dataGridType].gridKey}
                        title={dataGridOptions[dataGridType].title}
                        data={snippet}
                        isLoggedInOverlay={!isLoggedIn}
                        setAutoColumnWidth={
                            snippet.headerRows && snippet.headerRows[0] && snippet.headerRows[0].length > maxColumnCountAutoWidth
                                ? dataGridOptions[dataGridType].autoWidth
                                : true
                        }
                    />
                </div>
            )}
        />
    );

    const useStyles = makeStyles((theme: Theme) => {
        return createStyles({
            tooltip: {
                backgroundColor: '#fff',
                color: 'black',
                maxWidth: 450,
                fontSize: theme.typography.pxToRem(14),
                border: '1px solid #ccc',
                fontFamily: 'Akk Pro, Arial, sans-serif'
            }
        });
    });

    const infoIcon = () => {
        const { sampleDataDescription } = dataGridOptions[dataGridType];
        if (sampleDataDescription) {
            return (
                <>
                    <Media query={{ maxWidth: 1023 }}>
                        <>
                            <InfoIcon className="dataset-data-section__infoIcon" onClick={() => setPopupSample(true)} />
                            <Modal open={showPopupSample} onClose={() => setPopupSample(false)} isFullScreen scrollBody>
                                <div className="dataset-section-component__popup">
                                    <Popup title="" content={sampleDataDescription} />
                                </div>
                            </Modal>
                        </>
                    </Media>
                    <Media query={{ minWidth: 1024 }}>
                        <Tooltip
                            classes={useStyles(0)}
                            placement={window.innerWidth > 800 ? 'right' : 'top'}
                            arrow
                            disableFocusListener
                            disableTouchListener
                            title={sampleDataDescription}
                        >
                            <InfoIcon className="dataset-data-section__infoIcon" />
                        </Tooltip>
                    </Media>
                </>
            );
        }
        return null;
    };

    return (
        <section id={dataGridType} className="dataset-data-section page-section">
            <div className="page-section__inner">
                <SectionTitle
                    hash={dataGridType}
                    text={dataGridOptions[dataGridType].title}
                    subText={carouselState.totalSlides > 0 ? <span>{`${carouselState.activeSlide} of ${carouselState.totalSlides}`}</span> : null}
                    icon={dataGridOptions[dataGridType].icon}
                    onClick={scrollTo}
                    InfoIcon={infoIcon()}
                />
            </div>
            <React.Suspense fallback={<Loader minHeight="588px" />}>{isBundle ? bundleCarousel : noBundle}</React.Suspense>
            <div className="page-section__inner">
                {isLoggedIn && dataFilePath && (
                    <DownloadFile
                        text={filetypeName}
                        filetype={downloadFiletype}
                        url={cmsApiDocumentsUrl + dataFilePath || '#'}
                        keyPageId={dataGridOptions[dataGridType].keyPages}
                    />
                )}
            </div>
        </section>
    );
};

export default React.memo(datasetDataSection);
